import React from 'react';

// House SAA positioning dot table (mirrors the Portfolio Dashboard logic).
const MERGE = { 'EM Equities': 'International Equities' };
// combineEquities folds all equity classes into one 'Equities' row (tilts,
// exposures and series summed) — used by the Asset Class Overview word table.
const MERGE_COMBINED_EQ = {
  'EM Equities': 'Equities',
  'Australian Equities': 'Equities',
  'International Equities': 'Equities',
};
const STANDARD = new Set([
  'Australian Equities', 'International Equities', 'Equities', 'Corporate Debt',
  'Government Bonds', 'Total Growth vs Defensive', 'Cash',
]);
const COLS = ['strongUW', 'underweight', 'neutral', 'overweight', 'strongOW'];
const LABELS = {
  strongUW: 'Strong Underweight', underweight: 'Underweight', neutral: 'Neutral',
  overweight: 'Overweight', strongOW: 'Strong Overweight',
};
const ORDER = ['Equities', 'Australian Equities', 'International Equities', 'Real Estate', 'Infrastructure',
  'Government Bonds', 'Corporate Debt', 'Global Alpha', 'Cash'];
const DISPLAY = { 'International Equities': 'Global Equities', 'Real Estate': 'Property' };

export function getPosition(tilt, ac, exposure = null) {
  const strong = STANDARD.has(ac) ? 5 : 3;
  if (exposure != null && exposure < 0.5 && (exposure - tilt) > 0.5) return 'strongUW';
  if (tilt > strong) return 'strongOW';
  if (tilt >= 0.5) return 'overweight';
  if (Math.abs(tilt) < 0.5) return 'neutral';
  if (tilt <= -strong) return 'strongUW';
  return 'underweight';
}

// Derive display rows from the average-tilts payload. Shared by the dot table
// and the Asset Class Overview word table (same rows, same classification —
// the Portfolio Dashboard's SAA positioning logic) so the two cannot disagree.
// Returns null when the payload is unusable.
export function derivePositioningRows(tilts, { combineEquities = false } = {}) {
  if (!tilts || !tilts.latest) return null;

  const merge = combineEquities ? MERGE_COMBINED_EQ : MERGE;
  const latest = {}, latestTarget = {}, series = {};
  Object.entries(tilts.latest).forEach(([ac, t]) => { const k = merge[ac] || ac; latest[k] = (latest[k] || 0) + t; });
  Object.entries(tilts.latest_target || {}).forEach(([ac, t]) => { const k = merge[ac] || ac; latestTarget[k] = (latestTarget[k] || 0) + t; });
  Object.entries(tilts.series || {}).forEach(([ac, arr]) => {
    const k = merge[ac] || ac;
    if (!series[k]) series[k] = [...arr];
    else series[k] = series[k].map((v, i) => v + (arr[i] ?? 0));
  });

  const EPS = 0.05;
  const rows = Object.entries(latest).map(([ac, tilt]) => {
    const exposure = latestTarget[ac] ?? 0;
    const neutral = exposure - tilt;
    return {
      ac,
      display: DISPLAY[ac] || ac,
      tilt,
      exposure,
      neutral,
      position: getPosition(tilt, ac, exposure),
    };
  }).filter((r) => Math.abs(r.exposure) > EPS || Math.abs(r.neutral) > EPS)
    .sort((a, b) => (ORDER.indexOf(a.ac) + 1 || 99) - (ORDER.indexOf(b.ac) + 1 || 99));

  const g = tilts.growth;
  const gLast = g?.series?.length ? g.series[g.series.length - 1] : null;
  const growthRow = gLast == null ? null : {
    ac: 'Total Growth vs Defensive', display: 'Total Growth vs Defensive', tilt: gLast,
    position: getPosition(gLast, 'Total Growth vs Defensive'), isSummary: true,
  };

  return { rows, growthRow };
}

// Dot colour encodes the position, matching the Portfolio Dashboard's SAA table.
const DOT_COLOURS = {
  strongUW: '#8B0000',
  underweight: '#dc3545',
  neutral: '#072040',
  overweight: '#91CF9B',
  strongOW: '#2d8a4e',
};

export default function IpPositioningTable({ data }) {
  // Tailored client with no positioning suite chosen (Manage Positioning).
  if (data?.unset) return <div className="chart-placeholder">{data.message}</div>;
  const derived = derivePositioningRows(data);
  if (!derived) return <div className="chart-placeholder">No positioning data available</div>;
  const { rows, growthRow } = derived;
  if (rows.length === 0) return null;

  const renderRow = (r) => (
    <tr key={r.ac} className={r.isSummary ? 'ip-pos-summary' : ''}>
      <td className="ip-pos-ac">{r.display}</td>
      {COLS.map((c) => (
        <td key={c} className="ip-pos-dot-cell">
          {r.position === c
            ? <span className="ip-pos-dot" style={{ background: DOT_COLOURS[c] }} />
            : <span className="ip-pos-hollow">○</span>}
        </td>
      ))}
    </tr>
  );

  return (
    <table className="ip-pos-table">
      <thead>
        <tr>
          <th className="ip-pos-ac">Asset Class</th>
          {COLS.map((c) => <th key={c}>{LABELS[c]}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map(renderRow)}
        {growthRow && renderRow(growthRow)}
      </tbody>
    </table>
  );
}
